import { KeyboardArrowDown } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  ClickAwayListener,
  FormControl,
  FormLabel,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import ClientAvatar from 'components/shared/ClientAvatar';
import StageChip from 'components/shared/StageChip';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import RoutePaths from 'routes/RoutePaths';
import zeplinColor from 'theme/Color';

export interface OwnerOption {
  cognitoSub: string;
  fullName: string;
  profilePicture?: string;
}

type DropdownOption = string | OwnerOption;

interface DealDropdownProps<T extends DropdownOption> {
  options: T[];
  value: T | null;
  fieldName: string;
  setValue: (fieldName: string, value: T | null) => void;
  isBgDark?: boolean;
  isChip?: boolean;
}

const DealDropdown = <T extends DropdownOption>({
  options,
  value,
  setValue,
  fieldName,
  isBgDark = false,
  isChip = false,
}: DealDropdownProps<T>) => {
  const [isVisible, setIsVisible] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const hideDropdown = () => setIsVisible(false);

  const getOptionLabelText = (option: T | null): string => {
    if (!option) return '';
    return typeof option === 'string' ? option : option.fullName;
  };

  const getAvatarSrc = (option: T | null): string => {
    if (option && typeof option !== 'string') {
      return option.profilePicture || '';
    }
    return '';
  };

  const getOwnerId = (option: T | null): string => {
    if (option && typeof option !== 'string') {
      return option.cognitoSub;
    }
    return '';
  };

  return (
    <ClickAwayListener onClickAway={hideDropdown}>
      <FormControl>
        <Box ref={anchorRef} sx={{ display: 'inline-block' }}>
          <FormLabel onClick={() => setIsVisible((prev) => !prev)} sx={{ cursor: 'pointer' }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
              {isChip ? (
                <StageChip dealType={getOptionLabelText(value)} />
              ) : (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <ClientAvatar
                    profilePicture={getAvatarSrc(value)}
                    clientName={getOptionLabelText(value)}
                    sx={{ width: 24, height: 24 }}
                  />
                  <Typography
                    variant='responsiveHeading5'
                    component={Link}
                    to={`${RoutePaths.Team}/${getOwnerId(value)}`}
                    sx={{ color: zeplinColor.BasicBlack }}
                  >
                    {getOptionLabelText(value)}
                  </Typography>
                </Box>
              )}
              {<KeyboardArrowDown sx={{ color: isBgDark ? zeplinColor.Neutral1 : '' }} />}
            </Box>
          </FormLabel>
        </Box>

        <Popper open={isVisible} anchorEl={anchorRef.current} placement='bottom-start'>
          <Autocomplete
            open
            sx={{ backgroundColor: zeplinColor.Neutral1, width: '200px' }}
            value={value}
            renderInput={(params) => <TextField {...params} autoFocus />}
            onChange={(_, newValue) => {
              setValue(fieldName, newValue);
              if (newValue !== null) {
                hideDropdown();
              }
            }}
            options={options}
            getOptionLabel={(option) => getOptionLabelText(option)}
            getOptionKey={(option) => (typeof option === 'string' ? option : option.cognitoSub)}
            isOptionEqualToValue={(option, val) => {
              if (typeof option === 'string' && typeof val === 'string') return option === val;
              if (typeof option !== 'string' && typeof val !== 'string')
                return option.cognitoSub === val.cognitoSub;
              return false;
            }}
          />
        </Popper>
      </FormControl>
    </ClickAwayListener>
  );
};

export default DealDropdown;
